import sortBy from 'lodash.sortby';
import format from '../plugins/baseFormat';

/* Plans may come back without a name; the table still needs a Nome cell. */
const EMPTY_NAME = '—';
const EMPTY_VALUE = '-';

/**
 * Much+ global subscription plan.
 *
 * Plans are not configured per store: activation applies to all of
 * Delivery Much. Paid and free plans are two separate listings, split by the
 * `has_trial` query param — a free plan is one that carries a trial period,
 * and its benefits live in `trial_benefits` instead of `benefits`.
 *
 * There is always exactly 1 active paid plan, and 0 or 1 active free plan.
 */
export default class SubscriptionPlan {
  static get STATUS () {
    return {
      ENABLED: 'enabled',
      DISABLED: 'disabled',
    };
  }

  /**
   * @param {Object} initialData raw plan from `GET /subscription-plans`
   * @param {Object} options `hasTrial` comes from the request that returned
   *   the plan, so the split never depends on guessing from the payload
   */
  constructor (initialData = {}, { hasTrial = false, } = {},) {
    const {
      name,
      amount,
      status,
      benefits,
      trial_benefits,
      trial_days_amount,
      created_at,
      updated_at,
      ...rest
    } = initialData;

    this.name = name || EMPTY_NAME;
    this.status = status || SubscriptionPlan.STATUS.DISABLED;
    this.value = amount;
    this.created_at = created_at || null;
    this.updated_at = updated_at || null;

    this.hasTrial = Boolean(hasTrial,);
    this.isPaid = !this.hasTrial;
    this.isActive = this.status === SubscriptionPlan.STATUS.ENABLED;

    /* Grace period duration, only meaningful for free plans. */
    this.validity = trial_days_amount || 0;

    const couponPackage = this.pickCouponPackage(
      this.hasTrial ? trial_benefits : benefits,
    );
    const coupon = (couponPackage && couponPackage.coupon) || {};

    this.quantity = couponPackage ? couponPackage.quantity : EMPTY_VALUE;
    this.code = coupon.code || EMPTY_VALUE;
    this.discount_value = coupon.value;
    this.isPercentDiscount = coupon.percent === true;

    /*
     * Same as the coupon model: the discount cap only makes sense when the
     * discount is a percentage.
     */
    this.max_discount = this.isPercentDiscount ? coupon.max_discount : null;

    this.formattedType = this.isPercentDiscount ? '%' : 'R$';
    this.formattedPrice = this.formatMoney(amount,);
    this.formattedDiscount = this.formatDiscount(this.discount_value,);
    this.formattedMaxDiscount = this.formatMoney(this.max_discount,);
    this.createdAt = this.formatDate(created_at,);
    this.updatedAt = this.formatDate(updated_at,);

    Object.assign(this, rest,);
  }

  /*
   * The screen shows a single coupon per plan, but the contract allows
   * several benefits. Lowest `coupon.order` wins; array order breaks ties
   * (lodash.sortby is stable).
   */
  pickCouponPackage (benefits,) {
    const packages = (benefits || []).
      filter((benefit,) => benefit && benefit.coupon_package,).
      map((benefit,) => benefit.coupon_package,);

    if (!packages.length) {
      return null;
    }

    return sortBy(packages, (couponPackage,) => {
      const order = couponPackage.coupon && couponPackage.coupon.order;

      return order === null || order === undefined ? Infinity : order;
    },)[0];
  }

  formatMoney (value,) {
    if (value === null || value === undefined || value === '') {
      return EMPTY_VALUE;
    }

    return format.money(Number(value,),);
  }

  formatDiscount (value,) {
    if (value === null || value === undefined || value === '') {
      return EMPTY_VALUE;
    }

    if (this.isPercentDiscount) {
      return `${value}%`;
    }

    return this.formatMoney(value,);
  }

  formatDate (date,) {
    if (!date) {
      return '';
    }

    return new Date(date,).toLocaleDateString('pt-BR',);
  }
}
